import * as React from 'react';
import styled from 'styled-components';
import {
  IBlockData,
  IBlockResult,
} from '../../../data/providers/reports/blockReportProvider';
import { Entity, IRange } from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { Row } from '../../layout/Row';
import { ListAvatar } from '../../shared/List/ListAvatar';
import { Notable } from '../../shared/pdf/Notable';
import { EPdfMode, isPdf } from '../../shared/pdf/Pdf';
import { TrafficLight } from '../../shared/TrafficLight';
import { DataCell, DataCellColumn, DataColumn } from '../utils/Table';
import { BlockTrendTrigger } from './BlockTrendPopup';

interface IProps {
  blockResult: IBlockResult[];
  pdfMode: EPdfMode;
  range: IRange;
  rawValsView: boolean;
  shape?: number[];
}

export const BlockResult = (props: IProps): React.ReactElement => {
  /**
   * @description generates the individual entity cell w/ avatar for each athlete in the team/group
   * includes the date range for this athlete's data
   */
  const entityRenderer = (
    entity: Entity,
    data: IBlockData[]
  ): React.ReactElement => {
    // Get date range from first data item (they should be consistent across metrics)
    const dateStart = data[0]?.start;
    const dateEnd = data[0]?.end;

    return (
      <EntityCell>
        <EntityCellContent>
          <ListAvatar entity={entity} style={{ padding: '10px' }} />
          {dateStart && dateEnd && (
            <DateRangeCell>
              {dateStart} - {dateEnd}
            </DateRangeCell>
          )}
        </EntityCellContent>
      </EntityCell>
    );
  };

  /**
   * @description generates the individual data cells for each testType and metric combination
   * Entire cell is clickable to open trend popup
   */
  const dataRenderer = (
    data: IBlockData[],
    entity: Entity
  ): React.ReactElement[] => {
    return data.map((datum: IBlockData, index: number): React.ReactElement => {
      if (datum.percentage === null || datum.percentage === 0) {
        return (
          <DataCell
            key={index}
            style={{
              flex: 1,
              padding: '0px',
              borderTop: '1px solid var(--color-separator)',
            }}
          >
            <MetricValue>-</MetricValue>
          </DataCell>
        );
      } else {
        const baseVal = roundValue(datum.baseline, datum.roundingPrecision);
        const compVal = roundValue(datum.comparison, datum.roundingPrecision);
        const percVal = roundValue(datum.percentage, 1);
        const isNeg = datum.baseline < 0;

        return (
          <DataColumn
            key={index}
            style={{ flex: 1, borderTop: '1px solid var(--color-separator)' }}
          >
            <BlockTrendTrigger
              entity={entity}
              metricKey={datum.metric.value}
              metricName={datum.metric.label}
              entityId={entity.id}
              range={props.range}
              queryHash={datum.hash}
            >
              <DataCellColumn style={{ padding: '0px' }}>
                <ValueContainer>
                  {props.rawValsView ? (
                    <RawValuesRow>
                      <RawValue>{baseVal}</RawValue>
                      <RawValueDivider />
                      <RawValue>{compVal}</RawValue>
                    </RawValuesRow>
                  ) : (
                    <MetricValue>{percVal}%</MetricValue>
                  )}
                </ValueContainer>
                <TrafficLightWrapper>
                  <TrafficLight
                    isNeg={isNeg}
                    direction={datum.metric?.direction}
                    lowerCutoff={datum.metric?.changeCutoffs?.lowerCutoff}
                    upperCutoff={datum.metric?.changeCutoffs?.upperCutoff}
                    value={percVal}
                  />
                </TrafficLightWrapper>
              </DataCellColumn>
            </BlockTrendTrigger>
          </DataColumn>
        );
      }
    });
  };

  /**
   * @description generates each row of the block results table
   */
  const rowRenderer = (
    results: IBlockResult[],
    pdfMode: EPdfMode
  ): React.ReactElement[] => {
    return results.map((result: IBlockResult): React.ReactElement => {
      return (
        <Notable key={result.entity.id} pdf={isPdf(pdfMode)} borderBottom>
          <Row
            style={{
              alignItems: 'stretch',
            }}
          >
            {entityRenderer(result.entity, result.data)}
            {dataRenderer(result.data, result.entity)}
          </Row>
        </Notable>
      );
    });
  };

  return <div>{rowRenderer(props.blockResult, props.pdfMode)}</div>;
};

const EntityCell = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 150px;
  border: 1px solid var(--color-separator);
  border-left: none;
`;

const EntityCellContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  flex: 1;
`;

const DateRangeCell = styled.div`
  font-size: 0.85em;
  color: var(--color-secondary-text);
  padding: 5px 10px;
  margin-top: auto;
  border-top: 1px solid var(--color-separator);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 100%;
  box-sizing: border-box;
`;

const ValueContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 5px;
  min-height: 60px;
  flex: 1;
`;

const TrafficLightWrapper = styled.div`
  border-top: 1px solid var(--color-separator);
  width: 100%;
`;

const RawValuesRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const RawValue = styled.span`
  font-weight: bold;
  color: var(--color-metric-value);
  font-family: 'Roboto';
  text-shadow: 1px 1px 1px var(--color-text-shadow);
  @media only screen and (max-width: 1500px) {
    font-size: 1rem;
  }
  font-size: 1.5rem;
`;

const RawValueDivider = styled.div`
  width: 1px;
  height: 24px;
  background: var(--color-separator);
`;

const MetricValue = styled.h4<{ large?: boolean }>`
  font-weight: bold;
  color: var(--color-metric-value);
  font-family: 'Roboto';
  text-shadow: 1px 1px 1px var(--color-text-shadow);
  @media only screen and (max-width: 1500px) {
    font-size: 1rem;
  }
  font-size: 1.5rem;
`;
